import { ChakraProvider } from "@chakra-ui/react";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router";
import { NotFound } from "src/components/Feedback/NotFound";
import { ScheduledDowntime } from "src/components/Feedback/ScheduledDowntime";
import { BrandingProvider } from "src/components/Providers/BrandingProvider";
import { UnauthenticatedQueryClientProvider } from "src/components/Providers/UnauthenticatedQueryClientProvider";
import { DefaultBranding } from "src/schemas/Branding";
import { buildTheme } from "src/services/theme/themeBuilder";
import { BrandedChakraProvider } from "../components/Providers/BrandedChakraProvider";
import { WeglotInject } from "../plugins/weglot";
import { AuthorizedAvelaRoutes } from "./AuthorizedAvelaRoutes";
import { UnauthorizedAvelaRoutes } from "./UnauthorizedRoutes";

export default function App() {
  const scheduledDowntimeIsEnabled =
    import.meta.env.VITE_MAINTENANCE_MODE === "true";

  if (scheduledDowntimeIsEnabled) {
    return (
      <ChakraProvider theme={buildTheme(DefaultBranding)}>
        <ScheduledDowntime />
      </ChakraProvider>
    );
  }

  // react-router v7 data router: createBrowserRouter + RouterProvider.
  // Routes composed via createRoutesFromElements; used by AuthorizedAvelaRoutes, UnauthorizedAvelaRoutes.
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <UnauthenticatedQueryClientProvider>
            <BrandingProvider>
              <BrandedChakraProvider>
                <Outlet />
              </BrandedChakraProvider>
            </BrandingProvider>
          </UnauthenticatedQueryClientProvider>
        }
      >
        {AuthorizedAvelaRoutes}
        {UnauthorizedAvelaRoutes}
        <Route path="*" element={<NotFound />} />
      </Route>
    )
  );

  /**
   * Disable React Router v7 transition-wrapped navigations.
   *
   * Our PreserveState/Freeze implementation suspends the parent route to keep
   * its state alive while a nested route renders. When navigation runs inside a
   * React transition, React keeps the already-visible UI instead of committing
   * the new render when something suspends, which prevents the outlet from
   * rendering. Disabling transitions restores the intended behavior.
   * @see https://reactrouter.com/explanation/react-transitions
   */
  return (
    <WeglotInject>
      <RouterProvider router={router} useTransitions={false} />
    </WeglotInject>
  );
}
